/**
 * Minimal deterministic 2D noise suite.
 *
 * Value/gradient noise is enough here: terrain quality is dominated by fBm
 * layering, domain warping and ridging, not by the choice of basis function.
 * Everything is deterministic under `Math.fround`-safe f64 arithmetic so a
 * seed reproduces the exact same terrain on any machine, and the same code
 * also runs on the CPU fallback backend.
 */

/* -------------------------------------------------------------------------- */
/* Hashing                                                                    */
/* -------------------------------------------------------------------------- */

/** Deterministic 32-bit integer hash of a 2D lattice point + seed. */
export function hash2i(ix: number, iy: number, seed: number): number {
  let h = (ix | 0) * 0x27d4eb2d;
  h = (h ^ ((iy | 0) * 0x165667b1)) | 0;
  h = (h ^ (seed * 0x9e3779b1)) | 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
}

/** Hash to the unit interval. */
export function hash2f(ix: number, iy: number, seed: number): number {
  return hash2i(ix, iy, seed) / 4294967296;
}

/** Hash to (-1, 1). */
export function hash2s(ix: number, iy: number, seed: number): number {
  return hash2f(ix, iy, seed) * 2 - 1;
}

/** Seeded PRNG for non-grid decisions (preset jitter, import sampling...). */
export function mulberry32(seed: number): () => number {
  let a = (seed | 0) >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), 1 | t);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* -------------------------------------------------------------------------- */
/* Basis noise                                                                */
/* -------------------------------------------------------------------------- */

/** Quintic smoothstep — C2 continuous, avoids visible lattice creases. */
function quintic(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/**
 * Gradient (Perlin-style) noise on the integer lattice, output roughly in
 * [-1, 1]. Uses 8 fixed unit-ish gradients selected by hash.
 */
const GRAD_X = [1, -1, 1, -1, 1, -1, 0, 0];
const GRAD_Y = [1, 1, -1, -1, 0, 0, 1, -1];

export function perlin2(x: number, y: number, seed: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const u = quintic(fx);
  const v = quintic(fy);

  let n = 0;
  for (let j = 0; j <= 1; j++) {
    for (let i = 0; i <= 1; i++) {
      const g = hash2i(x0 + i, y0 + j, seed) & 7;
      const dx = fx - i;
      const dy = fy - j;
      const dot = GRAD_X[g] * dx + GRAD_Y[g] * dy;
      const w = (i === 0 ? 1 - u : u) * (j === 0 ? 1 - v : v);
      n += dot * w;
    }
  }
  // Empirical normalisation so the result stays close to [-1, 1].
  return n * 1.4;
}

/* -------------------------------------------------------------------------- */
/* fBm variants                                                               */
/* -------------------------------------------------------------------------- */

export interface FbmOptions {
  octaves: number;
  /** Frequency multiplier between octaves (lacunarity). */
  lacunarity: number;
  /** Amplitude multiplier between octaves (this is the UI "roughness"). */
  gain: number;
  /** Initial lattice frequency. */
  frequency: number;
  /** Rotate each octave to break lattice alignment artifacts. */
  rotate?: number;
  offset?: number;
}

const DEFAULT_FBM: FbmOptions = {
  octaves: 6,
  lacunarity: 2.02,
  gain: 0.5,
  frequency: 1,
  rotate: 0.37,
  offset: 0,
};

/** Standard fractal Brownian motion, output ~[-1, 1]. */
export function fbm2(x: number, y: number, seed: number, opts: Partial<FbmOptions> = {}): number {
  const o = { ...DEFAULT_FBM, ...opts };
  let amp = 1;
  let sum = 0;
  let norm = 0;
  let px = x * o.frequency;
  let py = y * o.frequency;
  const ca = Math.cos(o.rotate ?? 0);
  const sa = Math.sin(o.rotate ?? 0);
  for (let i = 0; i < o.octaves; i++) {
    sum += amp * perlin2(px, py, seed + i * 7919);
    norm += amp;
    amp *= o.gain;
    const nx = px * ca - py * sa;
    const ny = px * sa + py * ca;
    px = nx * o.lacunarity;
    py = ny * o.lacunarity;
  }
  return norm > 0 ? sum / norm : 0;
}

/**
 * Ridged multifractal. `1 - |noise|` creates sharp crests and is the main
 * reason ridged fBm reads as a mountain ridge rather than a lump.
 * Output ~[0, 1].
 */
export function ridged2(
  x: number,
  y: number,
  seed: number,
  opts: Partial<FbmOptions> = {},
): number {
  const o = { ...DEFAULT_FBM, ...opts };
  let amp = 1;
  let sum = 0;
  let norm = 0;
  let px = x * o.frequency;
  let py = y * o.frequency;
  const ca = Math.cos(o.rotate ?? 0);
  const sa = Math.sin(o.rotate ?? 0);
  for (let i = 0; i < o.octaves; i++) {
    const n = 1 - Math.abs(perlin2(px, py, seed + i * 6151));
    // Squaring sharpens the crest and flattens the valleys between ridges.
    sum += amp * n * n;
    norm += amp;
    amp *= o.gain;
    const nx = px * ca - py * sa;
    const ny = px * sa + py * ca;
    px = nx * o.lacunarity;
    py = ny * o.lacunarity;
  }
  return norm > 0 ? sum / norm : 0;
}

/**
 * Billowy noise: `|noise|` gives rounded, cloud-like masses. Used for the
 * rolling-hills and dune presets where ridges would look wrong.
 * Output ~[0, 1].
 */
export function billow2(x: number, y: number, seed: number, opts: Partial<FbmOptions> = {}): number {
  const o = { ...DEFAULT_FBM, ...opts };
  let amp = 1;
  let sum = 0;
  let norm = 0;
  let px = x * o.frequency;
  let py = y * o.frequency;
  for (let i = 0; i < o.octaves; i++) {
    sum += amp * Math.abs(perlin2(px, py, seed + i * 3253));
    norm += amp;
    amp *= o.gain;
    px *= o.lacunarity;
    py *= o.lacunarity;
  }
  return norm > 0 ? sum / norm : 0;
}

/* -------------------------------------------------------------------------- */
/* Shaping helpers                                                            */
/* -------------------------------------------------------------------------- */

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp((x - edge0) / (edge1 - edge0 || 1e-9), 0, 1);
  return t * t * (3 - 2 * t);
}

/**
 * Domain warping: displace the sample position by a second noise field.
 * This is what turns "noise blobs" into something that reads like eroded,
 * folded terrain.
 */
export function warp2(
  x: number,
  y: number,
  seed: number,
  amplitude: number,
  frequency = 1,
): [number, number] {
  if (amplitude <= 0) return [x, y];
  const wx = fbm2(x * frequency + 13.7, y * frequency - 7.1, seed + 1013, {
    octaves: 4,
    gain: 0.5,
    frequency: 1,
  });
  const wy = fbm2(x * frequency - 21.3, y * frequency + 5.9, seed + 2027, {
    octaves: 4,
    gain: 0.5,
    frequency: 1,
  });
  return [x + wx * amplitude, y + wy * amplitude];
}
